import type { ElementRef, OnChanges, OnInit } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../../enviroment/enviroment.staging';
import type { BookingStorePayload, DepositOptions } from '../../models/booking';
import type { Car } from '../../models/car';
import type { LocationDetail } from '../../models/location-detail';
import { RedirectService } from '../../services/redirect.service';
import { SharedModule } from '../../shared/shared.module';
import { WebFacade } from '../../store/web/web-facade.service';

@Component({
  selector: 'app-car-detail-card',
  imports: [SharedModule],
  templateUrl: './car-detail-card.component.html',
  styleUrl: './car-detail-card.component.scss',
})
export class CarDetailCardComponent implements OnInit, OnChanges {
  webfacadeService = inject(WebFacade);
  router = inject(Router);
  cdr = inject(ChangeDetectorRef);
  redirectionService = inject(RedirectService);

  @Input() car!: Car;
  @Input() dateFilters: Partial<LocationDetail> | null = null;
  @Output() closeCard = new EventEmitter<null>();
  selectedImageIndex = 0;
  @ViewChild('thumbsRef') thumbsRef!: ElementRef<HTMLDivElement>;
  safetyAndComfortFeatures: string[] = [];
  exteriorFeatures: string[] = [];
  interiorFeatures: string[] = [];
  pickupType: 'self_pickup' | 'car_delivery' = 'self_pickup';
  sameLocation = true;
  locationAutocompleteList$ = this.webfacadeService.locationAutoCompleteList$;
  pickupLocation: { name: string } | null = null;
  dropoffLocation: { name: string } | null = null;
  selectedSubscription: 'per_minute' | 'hourly' | 'daily' | 'per_week' | 'per_month' = 'per_minute';
  selectedPrice: string | null = null;
  selectedSubscriptionLeft: 'per_minute' | 'hourly' | 'daily' | 'per_week' | 'per_month' =
    'per_minute';
  selectedPriceLeft: string | null = null;
  additionalDriverPrice$ = this.webfacadeService.additionalDriverPrice$;
  unlimitedKMAllowancePrice$ = this.webfacadeService.unlimitedKMAllowancePrice$;
  isUnlimitedKM = true;
  locationDetail!: LocationDetail;

  ngOnInit(): void {
    if (this.car) {
      if (this.car.features && this.car.features.length > 0) {
        const FEATURE_DATA = this.car.features[0];

        this.safetyAndComfortFeatures = Array.isArray(FEATURE_DATA.safetyAndComfortFeatures)
          ? FEATURE_DATA.safetyAndComfortFeatures
          : [];

        this.exteriorFeatures = Array.isArray(FEATURE_DATA.exteriorFeatures)
          ? FEATURE_DATA.exteriorFeatures
          : [];

        this.interiorFeatures = Array.isArray(FEATURE_DATA.interiorFeatures)
          ? FEATURE_DATA.interiorFeatures
          : [];
      }
      // this.onSelectSubscription('per_minute')
      this.setDefaultSubscriptionAndPrice();
    }

    this.webfacadeService.locationDetail$.subscribe((res) => {
      this.locationDetail = res;
    });
  }

  setDefaultSubscriptionAndPrice(): void {
    const PRICE_ORDER = [
      { key: 'per_minute', value: this.car.perMinuteRate },
      { key: 'hourly', value: this.car.hourlyRate },
      { key: 'daily', value: this.car.dailyRate },
      { key: 'per_week', value: this.car.weeklyRate },
      { key: 'per_month', value: this.car.monthlyRate },
    ] as const;

    const FIRST_AVAILABLE = PRICE_ORDER.find((p) => Number(p.value) > 0);

    if (FIRST_AVAILABLE) {
      this.selectedSubscription = FIRST_AVAILABLE.key;
      this.selectedPrice = FIRST_AVAILABLE.value;
    } else {
      this.selectedPrice = null;
    }

    if (Number(this.car.dailyRate) > 0) {
      this.selectedSubscriptionLeft = 'daily';
      this.selectedPriceLeft = this.car.dailyRate;
    } else {
      this.selectedSubscriptionLeft = this.selectedSubscription;
      this.selectedPriceLeft = this.selectedPrice;
    }

    this.onSelectSubscription(this.selectedSubscription);
    this.cdr.detectChanges();
  }

  ngOnChanges(): void {
    this.selectedImageIndex = 0;
    this.setDefaultSubscriptionAndPrice();
  }

  onThumbClick(idx: number): void {
    this.selectedImageIndex = idx;
    this.scrollActiveThumbIntoView();
  }

  onPrev(): void {
    const TOTAL = this.car?.carImages?.length || 0;
    if (TOTAL === 0) {
      return;
    }
    this.selectedImageIndex = (this.selectedImageIndex - 1 + TOTAL) % TOTAL;
    this.scrollActiveThumbIntoView('prev');
  }

  onNext(): void {
    const TOTAL = this.car?.carImages?.length || 0;
    if (TOTAL === 0) {
      return;
    }
    this.selectedImageIndex = (this.selectedImageIndex + 1) % TOTAL;
    this.scrollActiveThumbIntoView('next');
  }

  private scrollActiveThumbIntoView(direction?: 'prev' | 'next'): void {
    const CONTAINER = this.thumbsRef?.nativeElement;
    if (!CONTAINER) {
      return;
    }

    const CHILDERN = CONTAINER.querySelectorAll('img.gallery-thumb');
    const ACTIVE = CHILDERN[this.selectedImageIndex] as HTMLElement | undefined;
    if (!ACTIVE) {
      return;
    }

    const C_RECT = CONTAINER.getBoundingClientRect();
    const A_RECT = ACTIVE.getBoundingClientRect();

    const IS_FULLY_VISIBLE = A_RECT.left >= C_RECT.left && A_RECT.right <= C_RECT.right;
    if (IS_FULLY_VISIBLE) {
      return;
    }

    const DELTA = direction === 'prev' ? -(C_RECT.width * 0.6) : C_RECT.width * 0.6;

    CONTAINER.scrollBy({ left: DELTA, behavior: 'smooth' });
  }

  comparePickupLocation = (o1: { name: string } | null, o2: { name: string } | null): boolean =>
    o1 && o2 ? o1.name === o2.name : o1 === o2;

  compareDropoffLocation = (o1: { name: string } | null, o2: { name: string } | null): boolean =>
    o1 && o2 ? o1.name === o2.name : o1 === o2;

  onPickupInput(event: Event): void {
    const VAL = (event.target as HTMLInputElement).value;
    this.pickupLocation = { name: VAL };
  }

  onSelectSubscription(option: 'per_minute' | 'hourly' | 'daily' | 'per_week' | 'per_month'): void {
    this.selectedSubscription = option;
    if (option === 'per_minute') {
      this.selectedPrice = this.car.perMinuteRate || null;
    } else if (option === 'hourly') {
      this.selectedPrice = this.car.hourlyRate || null;
    } else if (option === 'daily') {
      this.selectedPrice = this.car.dailyRate || null;
    } else if (option === 'per_week') {
      this.selectedPrice = this.car.weeklyRate;
    } else if (option === 'per_month') {
      this.selectedPrice = this.car.monthlyRate;
    }
  }

  adjustImage(event: Event): void {
    const IMG = event.target as HTMLImageElement;
    IMG.style.objectFit = 'cover';
    IMG.style.transform = 'scale(1) translateY(5%)';
  }

  onClose(): void {
    this.closeCard.emit(null);
  }

  onContinueClick(): void {
    let depositOption!: DepositOptions;
    const FINAL_OBJ: BookingStorePayload = {
      carDetail: this.car,
      selectedPrice: +this.selectedPrice!,
      package: this.selectedSubscription,
      extras: [],
      depositOption: depositOption,
      total: 0,
      depositAmount: 0,
      baseRent: 0,
      pickupType: this.pickupType,
      sameLocation: this.sameLocation,
      pickupLocation: this.pickupLocation?.name ?? '',
      dropoffLocation: this.dropoffLocation?.name ?? '',
      isUnlimitedKM: this.isUnlimitedKM,
    };
    const FILTERS = this.dateFilters ? { ...this.dateFilters } : this.locationDetail;
    this.webfacadeService.setCarDetail(FINAL_OBJ);
    this.webfacadeService.setLocationDetail(FILTERS as LocationDetail);
    setTimeout(() => {
      this.redirectionService.redirectWithEncryptedData(
        environment.ANGULAR_REDIRECT_URL,
        'web',
        'token',
        'auth/login'
      );
    }, 0);
    this.onClose();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByIndexSafety(index: number): number {
    return index;
  }

  trackByIndexExterior(index: number): number {
    return index;
  }

  trackByIndexInterior(index: number): number {
    return index;
  }

  trackByIndexLocations(index: number): number {
    return index;
  }

  getDiscountedPrice(price: number): number {
    if (this.car.hotDeal) {
      const VALID_PRICE = price ?? 0;
      const DISCOUNT = VALID_PRICE * 0.1;
      return VALID_PRICE - DISCOUNT;
    } else {
      return price;
    }
  }
}
